import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon } from 'lucide-react';
export function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    service: '',
    date: '',
    message: ''
  });
  const [smsConsent1, setSmsConsent1] = useState(false);
  const [smsConsent2, setSmsConsent2] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          service: '',
          date: '',
          message: ''
        });
        setSmsConsent1(false);
        setSmsConsent2(false);
      }, 5000);
    }, 1500);
  };
  const inputClasses = 'w-full bg-brand-charcoal border border-brand-charcoal focus:border-brand-gold rounded-sm px-4 py-3 text-brand-white placeholder-brand-champagne-light/30 focus:outline-none focus:ring-1 focus:ring-brand-gold transition-colors';
  return <main className="bg-brand-black min-h-screen" data-id="element-264">
      {/* Hero */}
      <section className="relative pt-36 pb-16 overflow-hidden" data-id="element-265">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-soft-black via-brand-black to-brand-black z-0" data-id="element-266" />
        <div className="absolute inset-0 z-0 opacity-15 pointer-events-none" data-id="element-267">
          <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-brand-gold to-transparent" data-id="element-268" />
          <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-brand-gold to-transparent" data-id="element-269" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-270">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-271">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-brand-white mb-5" data-id="element-272">
              Contact Us
            </h1>
            <div className="w-16 h-px bg-brand-gold mx-auto mb-6" data-id="element-273" />
            <p className="text-lg text-brand-champagne-light/80 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-274">
              Have questions or ready to book? Reach out to our team or request
              an appointment below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="pb-16" data-id="element-275">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-276">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-277">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-brand-charcoal/40 border border-brand-gold/15 p-6 rounded-sm" data-id="element-278">
              <MapPinIcon className="w-7 h-7 text-brand-gold mb-4" strokeWidth={1.5} data-id="element-279" />
              <h3 className="font-heading text-lg text-brand-white mb-2" data-id="element-280">
                Location
              </h3>
              <address className="not-italic text-sm text-brand-champagne-light/70 leading-relaxed" data-id="element-281">
                Deluxe MedSpa Beauty
                <br data-id="element-282" />
                30 SW 1st St #1001
                <br data-id="element-283" />
                Miami, FL 33132
              </address>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-brand-charcoal/40 border border-brand-gold/15 p-6 rounded-sm" data-id="element-284">
              <PhoneIcon className="w-7 h-7 text-brand-gold mb-4" strokeWidth={1.5} data-id="element-285" />
              <h3 className="font-heading text-lg text-brand-white mb-2" data-id="element-286">
                Phone
              </h3>
              <a href="tel:786-901-0611" className="text-sm text-brand-champagne-light/70 hover:text-brand-gold transition-colors" data-id="element-287">
                (786) 901-0611
              </a>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-brand-charcoal/40 border border-brand-gold/15 p-6 rounded-sm" data-id="element-288">
              <MailIcon className="w-7 h-7 text-brand-gold mb-4" strokeWidth={1.5} data-id="element-289" />
              <h3 className="font-heading text-lg text-brand-white mb-2" data-id="element-290">
                Email
              </h3>
              <a href="mailto:info@deluxe-medspa.com" className="text-sm text-brand-champagne-light/70 hover:text-brand-gold transition-colors" data-id="element-291">
                info@deluxe-medspa.com
              </a>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="bg-brand-charcoal/40 border border-brand-gold/15 p-6 rounded-sm" data-id="element-292">
              <ClockIcon className="w-7 h-7 text-brand-gold mb-4" strokeWidth={1.5} data-id="element-293" />
              <h3 className="font-heading text-lg text-brand-white mb-2" data-id="element-294">
                Business Hours
              </h3>
              <div className="text-sm text-brand-champagne-light/70 space-y-1" data-id="element-295">
                <p data-id="element-296">Mon–Fri: 10:00 AM – 6:00 PM</p>
                <p data-id="element-297">Sat: 10:00 AM – 2:00 PM</p>
                <p className="text-brand-gold/80" data-id="element-298">Sun: Closed</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Appointment Request Form */}
      <section className="pb-24" data-id="element-299">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-300">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-brand-soft-black border border-brand-gold/25 rounded-sm p-8 md:p-12 shadow-luxury relative overflow-hidden" data-id="element-301">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-40" data-id="element-302" />

            <div className="text-center mb-10" data-id="element-303">
              <h2 className="font-heading text-3xl md:text-4xl text-brand-white mb-4" data-id="element-304">
                Request Your Appointment
              </h2>
              <p className="text-brand-champagne-light/70 text-sm md:text-base max-w-2xl mx-auto" data-id="element-305">
                Fill out the form below to request an appointment. You may also
                choose to opt in to text messages for appointment updates and
                promotional offers.
              </p>
            </div>

            {isSuccess ? <div className="bg-brand-charcoal border border-brand-gold/50 p-8 text-center rounded-sm" data-id="element-306">
                <div className="w-16 h-16 bg-brand-gold/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-307">
                  <svg className="w-8 h-8 text-brand-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-308">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-309" />
                  </svg>
                </div>
                <h3 className="font-heading text-2xl text-brand-white mb-2" data-id="element-310">
                  Request Received
                </h3>
                <p className="text-brand-champagne-light/80" data-id="element-311">
                  Thank you for reaching out. A member of our team will contact
                  you shortly to confirm your appointment details.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-312">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-313">
                  <div data-id="element-314">
                    <label htmlFor="c-firstName" className="block text-sm font-medium text-brand-champagne-light mb-2" data-id="element-315">
                      First Name *
                    </label>
                    <input type="text" id="c-firstName" name="firstName" required value={formData.firstName} onChange={handleInputChange} className={inputClasses} placeholder="Jane" data-id="element-316" />
                  </div>
                  <div data-id="element-317">
                    <label htmlFor="c-lastName" className="block text-sm font-medium text-brand-champagne-light mb-2" data-id="element-318">
                      Last Name *
                    </label>
                    <input type="text" id="c-lastName" name="lastName" required value={formData.lastName} onChange={handleInputChange} className={inputClasses} placeholder="Doe" data-id="element-319" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-320">
                  <div data-id="element-321">
                    <label htmlFor="c-phone" className="block text-sm font-medium text-brand-champagne-light mb-2" data-id="element-322">
                      Phone Number *
                    </label>
                    <input type="tel" id="c-phone" name="phone" required value={formData.phone} onChange={handleInputChange} className={inputClasses} placeholder="(786) 555-1234" data-id="element-323" />
                  </div>
                  <div data-id="element-324">
                    <label htmlFor="c-email" className="block text-sm font-medium text-brand-champagne-light mb-2" data-id="element-325">
                      Email Address *
                    </label>
                    <input type="email" id="c-email" name="email" required value={formData.email} onChange={handleInputChange} className={inputClasses} placeholder="jane@example.com" data-id="element-326" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-327">
                  <div data-id="element-328">
                    <label htmlFor="c-service" className="block text-sm font-medium text-brand-champagne-light mb-2" data-id="element-329">
                      Preferred Service
                    </label>
                    <select id="c-service" name="service" value={formData.service} onChange={handleInputChange} className={`${inputClasses} appearance-none`} data-id="element-330">
                      <option value="" data-id="element-331">Select a service...</option>
                      <option value="Botox" data-id="element-332">Botox</option>
                      <option value="Dermal Fillers" data-id="element-333">Dermal Fillers</option>
                      <option value="Hydrafacial" data-id="element-334">Hydrafacial</option>
                      <option value="Laser Hair Removal" data-id="element-335">
                        Laser Hair Removal
                      </option>
                      <option value="Chemical Peel" data-id="element-336">Chemical Peel</option>
                      <option value="Microneedling" data-id="element-337">Microneedling</option>
                      <option value="Weight Loss" data-id="element-338">Weight Loss</option>
                      <option value="Other" data-id="element-339">Other</option>
                    </select>
                  </div>
                  <div data-id="element-340">
                    <label htmlFor="c-date" className="block text-sm font-medium text-brand-champagne-light mb-2" data-id="element-341">
                      Preferred Appointment Date
                    </label>
                    <input type="date" id="c-date" name="date" value={formData.date} onChange={handleInputChange} className={`${inputClasses} [color-scheme:dark]`} data-id="element-342" />
                  </div>
                </div>

                <div data-id="element-343">
                  <label htmlFor="c-message" className="block text-sm font-medium text-brand-champagne-light mb-2" data-id="element-344">
                    Message (Optional)
                  </label>
                  <textarea id="c-message" name="message" rows={4} value={formData.message} onChange={handleInputChange} className={`${inputClasses} resize-none`} placeholder="Any specific concerns or questions?" data-id="element-345" />
                </div>

                {/* SMS COMPLIANCE SECTION */}
                <div className="pt-6 mt-6 border-t border-brand-charcoal space-y-6" data-id="element-346">
                  <h3 className="text-brand-gold font-medium text-sm uppercase tracking-wider" data-id="element-347">
                    Communication Preferences
                  </h3>

                  <div className="flex items-start" data-id="element-348">
                    <div className="flex items-center h-5 mt-1" data-id="element-349">
                      <input id="c-smsConsent1" type="checkbox" checked={smsConsent1} onChange={e => setSmsConsent1(e.target.checked)} className="w-5 h-5 bg-brand-charcoal border-brand-gold/50 rounded text-brand-gold focus:ring-brand-gold focus:ring-offset-brand-black" data-id="element-350" />
                    </div>
                    <label htmlFor="c-smsConsent1" className="ml-3 text-sm text-brand-champagne-light/90 cursor-pointer leading-relaxed" data-id="element-351">
                      I agree to receive SMS text messages from Deluxe MedSpa
                      Beauty related to my inquiry, appointment scheduling,
                      appointment confirmations, reminders, and service-related
                      updates. Message frequency may vary. Msg &amp; data rates
                      may apply. Reply STOP to opt out or HELP for assistance.
                    </label>
                  </div>

                  <div className="flex items-start" data-id="element-352">
                    <div className="flex items-center h-5 mt-1" data-id="element-353">
                      <input id="c-smsConsent2" type="checkbox" checked={smsConsent2} onChange={e => setSmsConsent2(e.target.checked)} className="w-5 h-5 bg-brand-charcoal border-brand-gold/50 rounded text-brand-gold focus:ring-brand-gold focus:ring-offset-brand-black" data-id="element-354" />
                    </div>
                    <label htmlFor="c-smsConsent2" className="ml-3 text-sm text-brand-champagne-light/90 cursor-pointer leading-relaxed" data-id="element-355">
                      I agree to receive occasional promotional SMS text
                      messages from Deluxe MedSpa Beauty about specials, offers,
                      events, and new services. Message frequency may vary. Msg
                      &amp; data rates may apply. Reply STOP to opt out or HELP
                      for assistance.
                    </label>
                  </div>

                  <div className="bg-brand-charcoal/50 p-4 rounded-sm border border-brand-charcoal" data-id="element-356">
                    <p className="text-xs text-brand-champagne-light/60 leading-relaxed" data-id="element-357">
                      Consent is not a condition of purchase. Message frequency
                      may vary. Message and data rates may apply. Reply STOP to
                      unsubscribe or HELP for help. By submitting this form, you
                      agree to the{' '}
                      <Link to="/privacy-policy" className="text-brand-gold hover:underline" data-id="element-358">
                        Privacy Policy
                      </Link>
                      ,{' '}
                      <Link to="/terms-conditions" className="text-brand-gold hover:underline" data-id="element-359">
                        Terms &amp; Conditions
                      </Link>
                      , and{' '}
                      <Link to="/sms-terms" className="text-brand-gold hover:underline" data-id="element-360">
                        SMS Terms
                      </Link>
                      .
                    </p>
                  </div>
                </div>

                <div className="pt-4" data-id="element-361">
                  <button type="submit" disabled={isSubmitting} className="w-full md:w-auto px-10 py-4 border border-transparent text-base font-medium rounded-sm text-brand-black bg-brand-gold hover:bg-brand-gold-light transition-all duration-300 shadow-gold-glow disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer" data-id="element-362">
                    {isSubmitting ? 'Submitting...' : 'Request Appointment'}
                  </button>
                  <p className="mt-4 text-xs text-brand-champagne-light/50 text-center md:text-left" data-id="element-363">
                    A member of Deluxe MedSpa Beauty may contact you by phone,
                    email, or text regarding your request based on your selected
                    preferences.
                  </p>
                </div>
              </form>}
          </motion.div>
        </div>
      </section>
    </main>;
}